import { useState, type ReactElement, type ReactNode } from 'react';
import { Button, IconButton } from '../../ui-kit/Button/Button';
import { BareSelect } from '../../ui-kit/Field/Field';
import { Icon, type IconName } from '../../ui-kit/Icon/Icon';
import { Link, useT } from '../di';
import type { RouteKey } from '../routes';
import styles from './AppShell.module.scss';

export interface NavItem {
  to: RouteKey;
  params: Record<string, string>;
  label: string;
  icon: IconName;
  current: boolean;
  /** Счётчик у пункта (наступившие публикации). */
  badge?: number;
}

export interface NavGroup {
  title: string;
  items: readonly NavItem[];
  /** Администрирование платформы: внизу меню, отделено чертой и подписано иначе. */
  admin?: boolean;
}

export interface AppShellProps {
  /** Заголовок шапки на узком экране — название текущего блога. */
  title: string;
  groups: readonly NavGroup[];
  blogs: readonly { id: string; name: string }[];
  currentBlogId: string | null;
  onSwitchBlog(blogId: string): void;
  onLogout(): void;
  /** Меню на узком экране закрывается при смене страницы. */
  pageKey: string;
  /** Панель под шапкой во всю ширину области страницы — например, «Нет связи с сервером». */
  notice?: ReactNode;
  /**
   * Значок состояния (индикатор фоновых задач, TASK-UC-2): в шапке телефона — справа, в меню
   * десктопа — рядом с названием; список раскрывается от края, где он стоит.
   */
  status?(align: 'start' | 'end'): ReactNode;
  children: ReactNode;
}

/** Оболочка: боковая навигация по разделам (docs/scenarios.md) и область страницы. */
export function AppShell({ title, groups, blogs, currentBlogId, onSwitchBlog, onLogout, pageKey, notice, status, children }: AppShellProps): ReactElement {
  const { t } = useT();
  const [openFor, setOpenFor] = useState<string | null>(null);
  const open = openFor === pageKey;
  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <IconButton icon="menu" label={open ? t('nav.closeMenu') : t('nav.openMenu')} onClick={() => setOpenFor(open ? null : pageKey)} aria-expanded={open} />
        <span className={styles.topTitle}>{title}</span>
        {status ? <div className={styles.topStatus}>{status('end')}</div> : null}
      </header>
      <aside className={`${styles.sidebar} ${open ? styles.open : ''}`}>
        <div className={styles.brand}>
          <span className={styles.logo} aria-hidden="true">
            GR
          </span>
          <span className={styles.brandName}>{t('common.appName')}</span>
          {status ? <div className={styles.sideStatus}>{status('start')}</div> : null}
        </div>
        {blogs.length > 0 ? (
          <div className={styles.blogSwitch}>
            <BareSelect
              label={t('nav.switchBlog')}
              className={styles.blogSelect}
              value={currentBlogId ?? ''}
              onChange={(e) => {
                if (e.target.value) onSwitchBlog(e.target.value);
              }}
              options={[...(currentBlogId ? [] : [{ value: '', label: t('nav.switchBlog') }]), ...blogs.map((b) => ({ value: b.id, label: b.name }))]}
            />
          </div>
        ) : null}
        <nav aria-label={t('nav.main')} className={styles.nav}>
          {groups.map((group) => (
            <div key={group.title} className={group.admin ? styles.adminGroup : undefined}>
              <p className={`${styles.groupTitle} ${group.admin ? styles.adminTitle : ''}`}>
                {group.admin ? <Icon name="settings" size={14} /> : null}
                <span className={group.admin ? undefined : 'u-truncate'}>{group.title}</span>
              </p>
              <ul className={styles.list}>
                {group.items.map((item) => (
                  <li key={`${item.to}-${item.label}`}>
                    <Link to={item.to} params={item.params} className={`${styles.link} ${item.current ? styles.active : ''}`} ariaCurrent={item.current}>
                      <Icon name={item.icon} />
                      <span className="u-grow u-truncate">{item.label}</span>
                      {item.badge ? (
                        <span className={styles.badge} aria-label={t('nav.dueCount', { n: item.badge })}>
                          {item.badge}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <Button variant="ghost" icon="logout" className={styles.logout} onClick={onLogout}>
          {t('nav.logout')}
        </Button>
      </aside>
      {open ? <div className={styles.backdrop} onClick={() => setOpenFor(null)} aria-hidden="true" /> : null}
      <div className={styles.column}>
        {notice}
        <main className={styles.main} id="main">
          {children}
        </main>
      </div>
    </div>
  );
}
